import type {SystemStyleObject} from '@tempo/core';
import {getTypographyStyles} from '@tempo/core';

export type D4CTableSize = 'compact' | 'sm';
export type D4CTableVariant = 'onContent' | 'contained';

export type TableRecipeStyles = {
	table: SystemStyleObject;
	thead: SystemStyleObject;
	tbody: SystemStyleObject;
	th: SystemStyleObject;
	td: SystemStyleObject;
	caption: SystemStyleObject;
};

type TablePart = keyof TableRecipeStyles;
type TablePartsStyles = Partial<Record<TablePart, SystemStyleObject>>;

function mergeSx(
	...styles: Array<SystemStyleObject | undefined>
): SystemStyleObject {
	return Object.assign({}, ...styles.filter(Boolean));
}

const BASE_STYLES: TablePartsStyles = {
	table: {
		borderCollapse: 'collapse',
	},
	th: {
		...getTypographyStyles('body.xs.default'),
		color: 'text.neutral.secondary.default',
		bg: 'background.neutral.secondary.default',
		px: {base: 'spacing.l', md: 'spacing.xl'},
		py: {base: 'spacing.m', md: 'spacing.l'},
	},
	td: {
		...getTypographyStyles('body.2xs.default'),
		px: {base: 'spacing.l', md: 'spacing.xl'},
		py: {base: 'spacing.m', md: 'spacing.l'},
	},
};

const SIZE_STYLES: Record<D4CTableSize, TablePartsStyles> = {
	compact: {
		th: {
			px: {base: 'spacing.s', md: 'spacing.m'},
			py: {base: 'spacing.s', md: 'spacing.m'},
			lineHeight: '4',
			fontSize: 'xs',
		},
		td: {
			...getTypographyStyles('body.2xs.default'),
			px: {base: 'spacing.s', md: 'spacing.m'},
			py: {base: 'spacing.s', md: 'spacing.m'},
		},
		caption: {
			px: '2',
			py: '2',
			fontSize: 'xs',
		},
	},
	sm: {
		th: {
			px: {base: 'spacing.s', sm: 'spacing.m', md: 'spacing.xl'},
			py: {base: 'spacing.m', md: 'spacing.l'},
		},
		td: {
			...getTypographyStyles('body.2xs.default'),
			px: {base: 'spacing.s', sm: 'spacing.m', md: 'spacing.xl'},
			py: {base: 'spacing.m', md: 'spacing.l'},
		},
		caption: {
			px: '2',
			py: '2',
			fontSize: 'xs',
		},
	},
};

const VARIANT_STYLES: Record<D4CTableVariant, TablePartsStyles> = {
	onContent: {},
	contained: {
		table: {
			borderRadius: 'md',
			borderColor: 'border.neutral.primary.default',
			borderWidth: '1px',
			borderStyle: 'solid',
			overflow: 'hidden',
			borderCollapse: 'separate',
			borderSpacing: 0,
		},
		thead: {
			'& tr > th': {
				borderBottomStyle: 'solid',
				borderBottomWidth: '1px',
				borderBottomColor: 'border.neutral.primary.default',
			},
		},
		tbody: {
			'& tr > td': {
				borderBottomStyle: 'solid',
				borderBottomWidth: '1px',
				borderBottomColor: 'border.neutral.primary.default',
			},
			'& tr:last-child > td': {
				border: 'none',
			},
		},
	},
};

export function resolveD4CTableStyles(
	size: D4CTableSize = 'sm',
	variant?: D4CTableVariant,
): TableRecipeStyles {
	const variantStyles = variant ? VARIANT_STYLES[variant] : undefined;

	const getPart = (part: TablePart): SystemStyleObject =>
		mergeSx(
			BASE_STYLES[part],
			variantStyles?.[part],
			SIZE_STYLES[size]?.[part],
		);

	return {
		table: getPart('table'),
		thead: getPart('thead'),
		tbody: getPart('tbody'),
		th: getPart('th'),
		td: getPart('td'),
		caption: getPart('caption'),
	};
}
